import React, { useState, useEffect } from 'react';
import { 
  createIdentity, 
  importIdentity, 
  saveIdentityToStorage, 
  loadIdentityFromStorage,
  clearIdentityFromStorage
} from '../utils/cryptoIdentity';

const IdentityManager = ({ onIdentitySelect }) => {
  const [identity, setIdentity] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [importError, setImportError] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [activeTab, setActiveTab] = useState('create');

  // Load identity from localStorage on component mount
  useEffect(() => {
    const storedIdentity = loadIdentityFromStorage();
    if (storedIdentity) {
      setIdentity(storedIdentity);
    }
  }, []);

  // Pass identity to parent component when it changes
  useEffect(() => {
    if (identity && onIdentitySelect) {
      console.log("Identity changed, passing to parent:", identity.displayName);
      onIdentitySelect(identity);
    }
  }, [identity, onIdentitySelect]);

  const handleCreateIdentity = (e) => {
    e.preventDefault();
    const newIdentity = createIdentity(displayName);
    setIdentity(newIdentity);
    saveIdentityToStorage(newIdentity);
    setDisplayName('');
  };

  const handleImportIdentity = (e) => {
    e.preventDefault();
    
    if (!secretKey) {
      setImportError('Secret key is required');
      return;
    }
    
    try {
      const importedIdentity = importIdentity(secretKey, displayName);
      setIdentity(importedIdentity);
      saveIdentityToStorage(importedIdentity);
      setSecretKey('');
      setDisplayName('');
      setImportError('');
    } catch (error) {
      setImportError(error.message || 'Failed to import identity');
    }
  };

  const handleUpdateDisplayName = (e) => {
    e.preventDefault();
    
    if (!displayName || !identity) {
      return;
    }
    
    // Create updated identity with new display name
    const updatedIdentity = { ...identity, displayName };
    setIdentity(updatedIdentity);
    saveIdentityToStorage(updatedIdentity);
    setDisplayName('');
  };

  const handleClearIdentity = () => {
    clearIdentityFromStorage();
    setIdentity(null);
    setDisplayName('');
    setSecretKey('');
  };

  return (
    <div className="card">
      <div className="card-header">
        <h3>Your Cryptographic Identity</h3>
      </div>
      <div className="card-body">
        {identity ? (
          <div>
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#f8f9fa', 
              borderRadius: '0.5rem',
              border: '1px solid #dee2e6',
              marginBottom: '1rem'
            }}>
              <h4>Current Identity</h4>
              
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>Display Name:</div>
                <div style={{ 
                  padding: '0.5rem', 
                  backgroundColor: 'white', 
                  borderRadius: '0.25rem',
                  border: '1px solid #dee2e6'
                }}>{identity.displayName}</div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>Ethereum Address:</span> 
                  <span style={{ fontWeight: 'normal', fontSize: '0.85rem' }}>(Verification ID)</span>
                  <span 
                    className="badge"
                    style={{ 
                      backgroundColor: '#6c5ce7', 
                      color: 'white',
                      fontSize: '0.75rem',
                      padding: '0.2rem 0.4rem'
                    }}
                  >
                    EVM Compatible
                  </span>
                  <span 
                    style={{ 
                      cursor: 'help',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '18px',
                      height: '18px',
                      borderRadius: '50%',
                      backgroundColor: '#e0e0e0',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      color: '#333'
                    }}
                    title="This address is compatible with Ethereum and other EVM blockchains. It can be used for holding digital assets and interacting with blockchain networks."
                    onClick={() => {
                      alert(
                        "EVM Compatible Address\n\n" +
                        "This address follows the same format used by Ethereum and other EVM-compatible blockchains.\n\n" +
                        "It can be used for:\n" +
                        "• Holding digital assets (tokens, NFTs)\n" +
                        "• Interacting with smart contracts\n" +
                        "• Connecting to Web3 applications\n" +
                        "• Signing transactions on compatible networks\n\n" +
                        "Networks supporting this format include Ethereum, Polygon, Arbitrum, Optimism, Base, and many others."
                      );
                    }}
                  >
                    i
                  </span>
                </div>
                <div style={{ 
                  padding: '0.5rem', 
                  backgroundColor: 'white', 
                  borderRadius: '0.25rem',
                  border: '1px solid #dee2e6',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  color: '#2c3e50',
                  overflowWrap: 'break-word'
                }}>{identity.ethAddress}</div>
                <div style={{ fontSize: '0.8rem', marginTop: '0.25rem', color: 'var(--gray-600)' }}>
                  This cryptographic address verifies your identity, validates your documents, and can be used with blockchain networks
                </div>
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem', display: 'flex', alignItems: 'center' }}>
                  Secret Key: <span style={{ 
                    fontWeight: 'normal', 
                    fontSize: '0.85rem', 
                    color: '#dc3545',
                    marginLeft: '0.5rem'
                  }}>(keep private)</span>
                </div>
                <div style={{ 
                  padding: '0.5rem', 
                  backgroundColor: 'white', 
                  borderRadius: '0.25rem',
                  border: '1px solid #dee2e6',
                  fontFamily: 'monospace',
                  fontSize: '0.85rem',
                  overflowWrap: 'break-word',
                  position: 'relative'
                }}>
                  <div style={{ filter: showSecretKey ? 'none' : 'blur(5px)' }}>
                    {identity.secretKey}
                  </div>
                  <button 
                    className="btn btn-sm btn-secondary"
                    style={{ 
                      position: 'absolute', 
                      top: '0.25rem', 
                      right: '0.25rem'
                    }}
                    onClick={() => setShowSecretKey(!showSecretKey)}
                  >
                    {showSecretKey ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div className="alert alert-danger mt-2" style={{ padding: '0.5rem', fontSize: '0.9rem' }}>
                  <strong>Warning:</strong> Never share your secret key with anyone. 
                  It is used to cryptographically sign your documents and prove your identity.
                </div>
              </div>
              
              <div className="mt-3" style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '0.25rem', border: '1px solid #dee2e6' }}>
                <h4>Update Display Name</h4>
                <form onSubmit={handleUpdateDisplayName}>
                  <div className="form-group">
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className="form-control"
                      placeholder="New display name"
                    />
                  </div>
                  <button type="submit" className="btn btn-primary mt-2">
                    Update Name
                  </button>
                </form>
              </div>
              
              <div className="mt-3">
                <button 
                  onClick={handleClearIdentity} 
                  className="btn btn-danger"
                >
                  Clear Identity
                </button>
              </div>
            </div>
            
            <div className="alert alert-success">
              <p className="mb-0">
                <strong>Identity ready for use!</strong> Your cryptographic identity will be used to verify ownership of your documentation.
              </p>
            </div>
          </div>
        ) : (
          <div>
            <div className="alert alert-info mb-3">
              <p>
                <strong>Why use a cryptographic identity?</strong> Adding an identity to your documentation:
              </p>
              <ul className="mb-0">
                <li>Creates a verifiable record of document ownership</li>
                <li>Links your documentation to a permanent Ethereum-compatible address</li>
                <li>Enables verification of your documentation on the decentralized network</li>
                <li>Prevents tampering and establishes authenticity</li>
              </ul>
            </div>
            
            <div className="nav nav-tabs">
              <button 
                className={`nav-link ${activeTab === 'create' ? 'active' : ''}`} 
                onClick={() => setActiveTab('create')}
              >
                Create New Identity
              </button>
              <button 
                className={`nav-link ${activeTab === 'import' ? 'active' : ''}`}
                onClick={() => setActiveTab('import')}
              >
                Import Existing Identity
              </button>
            </div>
            
            <div className="tab-content p-3 border border-top-0 rounded-bottom mb-3">
              {/* Create New Identity Tab */}
              {activeTab === 'create' && (
                <div>
                  <p>
                    Create a new cryptographic identity to link with your documentation. 
                    This identity is stored only on your device.
                  </p>
                  <form onSubmit={handleCreateIdentity}>
                    <div className="form-group mb-3">
                      <label className="form-label">Display Name (optional):</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="form-control"
                        placeholder="Your name or pseudonym"
                      />
                      <div className="form-text text-muted">
                        You can use a pseudonym to remain anonymous
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary">
                      Create Identity
                    </button>
                  </form>
                </div>
              )}
              
              {/* Import Existing Identity Tab */}
              {activeTab === 'import' && (
                <div>
                  <p>
                    If you have previously created an identity in the Decentralized Sharing app, 
                    you can import it here.
                  </p>
                  
                  <form onSubmit={handleImportIdentity}>
                    <div className="form-group mb-3">
                      <label className="form-label">Secret Key:</label>
                      <textarea
                        value={secretKey}
                        onChange={(e) => setSecretKey(e.target.value)}
                        className="form-control font-monospace"
                        placeholder="Paste your secret key here"
                        rows={3}
                      />
                    </div>
                    
                    <div className="form-group mb-3">
                      <label className="form-label">Display Name (optional):</label>
                      <input
                        type="text"
                        value={displayName}
                        onChange={(e) => setDisplayName(e.target.value)}
                        className="form-control"
                        placeholder="Use existing or set new name"
                      />
                      <div className="form-text text-muted">
                        Leave blank to use the original display name
                      </div>
                    </div>
                    
                    {importError && (
                      <div className="alert alert-danger mb-3">
                        <p className="mb-0">{importError}</p>
                      </div>
                    )}
                    
                    <button type="submit" className="btn btn-primary">
                      Import Identity
                    </button>
                  </form>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default IdentityManager;